import { List, ListItem, Stack } from '@mui/joy';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';

import { ShipSelection } from 'quotes/components/CustomerQuoteUpsert/ShipSelectionDateFields/enums';
import { EARLIEST_UNFROZEN_ORDER_STATUSES, LOCKED_ORDER_STATUSES } from 'quotes/constants';
import { OrderType, PricingStrategy, QuoteStatus, QuoteVersionType } from 'quotes/enums';
import { ICustomerQuote } from 'quotes/types';
import { Color } from 'theme/enums';

import {
  getInactiveProjectLinkWarningTexts,
  isInactiveProjectLinkWarning,
} from './projectLinkWarnings';

export const isOrderEditLocked = (status?: QuoteStatus): boolean =>
  !!status && LOCKED_ORDER_STATUSES.includes(status);

export const isNoDeliveryDate = (shipSelection?: string | null): boolean =>
  !shipSelection || shipSelection === ShipSelection.NO_DELIVERY_DATE;

// No Delivery Date submission day = the user's LOCAL day, shown in the drawer and sent on submit.
// Same day unless the drawer stays open past local midnight (newer day sent; BE accepts either).
export const getNoDeliveryDateSubmissionDay = (): Dayjs => dayjs().endOf('day');

// Mirrors BE Quote_RecordHelper::processNoDeliveryDateEarliest (is_order +
// alternate_version_enabled gate + NoDeliveryDateHelper::isEarliestDerived).
// For these the API returns the SERVER's today.
export const isNoDeliveryDateEarliestDerived = (quote: ICustomerQuote): boolean =>
  Boolean(quote.isOrder) &&
  Boolean(quote.alternateVersionEnabled) &&
  isNoDeliveryDate(quote.shipSelection) &&
  !!quote.status &&
  EARLIEST_UNFROZEN_ORDER_STATUSES.includes(quote.status);

// Unsubmitted No Delivery Date order ⇒ local today; otherwise the stored value (frozen once
// submitted).
export const getDefaultEarliestAcceptableDate = (quote: ICustomerQuote): Dayjs | null => {
  if (isNoDeliveryDateEarliestDerived(quote)) {
    return getNoDeliveryDateSubmissionDay();
  }

  return quote.earliestAcceptableDate ? dayjs(quote.earliestAcceptableDate) : null;
};

export const getOrderTypeLabels = (): Record<OrderType, string> => ({
  [OrderType.BILL_AND_HOLD]: t('labelOrderTypeBillAndHold'),
  [OrderType.BILLABLE]: t('labelOrderTypeBillable'),
  [OrderType.HOLD_FOR_RELEASE]: t('labelOrderTypeHoldForRelease'),
  [OrderType.SAMPLE_ORDER]: t('labelOrderTypeSampleOrder'),
});

export const getShipSelectionLabels = (): Record<ShipSelection, string> => ({
  [ShipSelection.NO_DELIVERY_DATE]: t('labelShipSelectionNoDeliveryDate'),
  [ShipSelection.DELIVERY_BY]: t('labelShipSelectionDeliveryBy'),
  [ShipSelection.DELIVERY_WITHIN]: t('labelShipSelectionDeliveryWithin'),
  [ShipSelection.DELIVERY_ON]: t('labelShipSelectionDeliveryOn'),
  [ShipSelection.DELIVERY_AFTER]: t('labelShipSelectionDeliveryAfter'),
});

export const getPricingStrategyLabels = (): Record<PricingStrategy, string> => ({
  [PricingStrategy.EXTERNAL]: t('labelPricingStrategyExternal'),
  [PricingStrategy.PRICING_PROFILE]: t('labelPricingStrategyPricingProfile'),
});

export const getQuoteVersion = (quote: ICustomerQuote): QuoteVersionType => {
  if (!quote) {
    return null;
  }

  const draftStatuses = [
    QuoteStatus.DRAFT,
    QuoteStatus.ORDER_DRAFT_CUSTOMER,
    QuoteStatus.ORDER_DRAFT_VENDOR,
  ];

  if (quote.draftVersionUpdatedAt !== null) {
    return QuoteVersionType.DRAFT;
  }

  if (draftStatuses.includes(quote.status) && quote.versionNumber === 1) {
    return QuoteVersionType.DRAFT;
  }

  return QuoteVersionType.RELEASE;
};

export const getQuoteVersionNumber = (
  quote: ICustomerQuote | null,
  quoteVersionType = QuoteVersionType.RELEASE
): number => {
  if (!quote) {
    return 1;
  }

  if (QuoteVersionType.RELEASE === quoteVersionType) {
    return quote.versionNumber || 1;
  }

  return quote.draftVersionNumber || quote.versionNumber + 1;
};

export const processSubmitOrderErrorMessages = (
  serverErrorData
): {
  title?: string;
  message: JSX.Element;
  displayEditShipToAddressButton: boolean;
} => {
  const responseMessage = serverErrorData?.responseMessage;
  const responseDescription = serverErrorData?.responseDescription;
  const errorMessage = serverErrorData?.error || t('serverError');

  let displayEditShipToAddressButton = false;

  const errorMessages = [];
  (serverErrorData?.errors || []).map((error) => {
    if (isInactiveProjectLinkWarning(error?.code)) {
      const { warning, action } = getInactiveProjectLinkWarningTexts(error.code);

      errorMessages.push(`${warning} ${action}`);

      return;
    }

    let message = error?.message;
    const editShipToAddressButtonReactTag = 'editShipToAddressButtonReactTag';

    if (error?.message.includes(editShipToAddressButtonReactTag)) {
      displayEditShipToAddressButton = true;

      message = error?.message.replace(editShipToAddressButtonReactTag, '');
    }

    errorMessages.push(message);
  });

  const responseDescriptionLines =
    typeof responseDescription === 'string'
      ? responseDescription
          .split(/\r?\n/)
          .map((line: string) => line.trim())
          .filter(Boolean)
      : [];

  const messageItems = [...responseDescriptionLines, ...errorMessages];

  return {
    title: responseMessage,
    message: (
      <Stack spacing={1}>
        {!responseMessage && errorMessage}

        {messageItems.length > 0 && (
          <List marker="disc" sx={{ lineHeight: '21px' }}>
            {messageItems.map((item: string) => (
              <ListItem key={item} color={Color.DANGER} sx={{ fontWeight: 500 }}>
                {item}
              </ListItem>
            ))}
          </List>
        )}
      </Stack>
    ),
    displayEditShipToAddressButton,
  };
};
